import {isNullOrZero, parseNumber} from '@benchling/js-utils';
import React from 'react';

import {Container, ContainerSummary} from 'constants/SampleConstants';
import {
  containerQuantityUnitTypePredicate,
  getCompatibilityFilterFromSymbols,
  getUnitFromCacheBySymbol,
} from 'inventory/InventoryUnitUtil';
import {ConfirmTransferQuantitiesDataRow} from 'samples/transfers/TransferModalUtil';
import Col from 'ui-library/bs/Col';
import Row from 'ui-library/bs/Row';
import SearchableUnitDropdown from 'units/SearchableUnitDropdown';
import {GenericEvent} from 'util/Types';

import {MeasurementValue} from './Measurements';
import * as TransferUtil from './TransferUtil';
interface Props {
  index: number;
  isInputTypeContainer: boolean;
  transferQuantityValue: number | null;
  finalOutputQuantityValue: number | null;
  initialOutputContainerState: ContainerSummary | null;
  outputContainer: Container;
  onUpdate: (index: number, changes: Partial<ConfirmTransferQuantitiesDataRow>) => void;
}

/**
 * Represents a row (a container being transferred into) in ConfirmTransferQuantitiesPane table.
 * Each row lets users specify the quantity to be transferred from the input sample into the row's output
 * container, and a final quantity for the row's output container.
 *
 * When the final quantity is higher than (quantity transferred from input) + (original quantity in output),
 * benchling will do an additional content-less "dilution" transfer to top up the output container quantity
 * to that level.
 *
 * The units that can be selected for the transfer are constrained by the output container's original units.
 */
function ConfirmTransferQuantityRow(props: Props): JSX.Element {
  function _handleUpdateTransferQuantity(e: GenericEvent): void {
    props.onUpdate(props.index, {transferQuantityValue: parseNumber(e.target.value)});
  }

  function _handleUpdateFinalQuantity(e: GenericEvent): void {
    props.onUpdate(props.index, {finalOutputQuantityValue: parseNumber(e.target.value)});
  }

  function _handleUpdateUnits(units: string | null): void {
    props.onUpdate(props.index, {
      outputContainer: {
        ...props.outputContainer,
        volume: TransferUtil.updateQuantityUnits(props.outputContainer.quantity, units),
        quantity: TransferUtil.updateQuantityUnits(props.outputContainer.quantity, units),
      },
    });
  }

  const computedFinalOutputQuantityValue = TransferUtil.addMeasurementValues(
    props.outputContainer.quantity.value,
    props.transferQuantityValue
  );
  const transferUnits = props.outputContainer.quantity.units;
  const initialOutputUnits = props.initialOutputContainerState?.quantity.units ?? null;

  return (
    <Row className="transferModal-tableRow">
      <Col xs={2} className="u-word-break-word">
        {props.outputContainer.barcode}
      </Col>
      <Col xs={2}>{props.outputContainer.name}</Col>
      <Col xs={2}>
        <MeasurementValue value={props.outputContainer.quantity.value} />
        {props.outputContainer.quantity.value != null && <span> {transferUnits}</span>}
      </Col>
      <Col xs={2}>
        <input
          min={0}
          name="transfer-quantity"
          onChange={_handleUpdateTransferQuantity}
          step="any"
          type="number"
          value={props.transferQuantityValue == null ? '' : props.transferQuantityValue}
        />
      </Col>
      <Col xs={2}>
        {props.isInputTypeContainer ? (
          <input
            min={(props.transferQuantityValue ?? 0) + (props.outputContainer.quantity.value ?? 0)}
            name="final-quantity"
            onChange={_handleUpdateFinalQuantity}
            required={Boolean(props.outputContainer.quantity.value)}
            step="any"
            type="number"
            value={props.finalOutputQuantityValue == null ? '' : props.finalOutputQuantityValue}
          />
        ) : (
          <MeasurementValue value={computedFinalOutputQuantityValue} />
        )}
      </Col>
      <Col xs={2}>
        <SearchableUnitDropdown
          displaySelectionAsSymbol={true}
          includeNone={isNullOrZero(props.outputContainer.quantity.value)}
          selectedId={transferUnits ? getUnitFromCacheBySymbol(transferUnits).id : null}
          onSelect={(u) => {
            _handleUpdateUnits(u?.symbol ?? null);
          }}
          unitTypeFilter={
            initialOutputUnits
              ? getCompatibilityFilterFromSymbols([initialOutputUnits])!
              : containerQuantityUnitTypePredicate
          }
        />
      </Col>
    </Row>
  );
}

export default ConfirmTransferQuantityRow;
